import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
// import { useSelector } from "react-redux";
import Panel from "../../../components/shared/Panel";
import { Flex, Heading } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import DashboardLimits from "./partials/DashboardLimits";
import DashboardCaseAcceptance from "./partials/DashboardCaseAcceptance";
import DashboardTreatmentPlansNew from "./partials/DashboardTreatmentPlansNew";
import DashboardMonthlyStacked from "./partials/DashboardMonthlyStacked";
import DashboardServices from "./partials/DashboardServices";
import useThemeColors from "../../../hooks/useThemeColors";
import DashboardMoreStats from "./partials/DashboardMoreStats";
// import DashboardCaseAcceptanceAlt from "./partials/DashboardCaseAcceptanceAlt";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import UserNoAccess from "../../../components/partials/UserNoAccess";
import useIsMobile from "../../../hooks/useIsMobile";
import Joyride from "react-joyride";
import { useTour } from "../../../hooks/useTour";

const DASHBOARD_TOUR_ENABLED = false;

const DashboardNew = () => {
  // GENERAL
  const { slug } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { whiteMode, backgroundLightMode } = useThemeColors();

  // STATE
  const { shouldRun, markAsSeen } = useTour("dashboard");
  const [showTourStart, setShowTourStart] = React.useState(false);
  const [runTour, setRunTour] = React.useState(false);

  const tourSteps = [
    {
      target: ".dashboard__case-acceptance",
      content: "Track how many plans get accepted by patients.",
      disableBeacon: true,
    },
    {
      target: ".dashboard__limits",
      content: "See your plan usage and upgrade if needed.",
      disableBeacon: true,
    },
    {
      target: ".dashboard__plans-pie",
      content: "Monitor your treatment plan status.",
      disableBeacon: true,
    },
  ];

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  // console.log("Dashboard component rendered. userInfo:", userInfo);

  // HANDLERS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasDashboardAccess = currentTenantAccess?.permissions?.dashboard?.on;
  const hasSubscriptionAccess =
    currentTenantAccess?.permissions?.settings?.modules;

  const [noAcesssAll, setNoAccessAll] = React.useState(false);

  // USEEFFECT
  useEffect(() => {
    window.scrollTo(0, 0);

    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    console.log("perms", perms);

    if (perms.dashboard?.on) return; // ✅ allowed, stay on dashboard

    // Redirect to first allowed section
    if (perms.patients?.on) {
      navigate(`/${slug}/patients`);
    } else if (perms.treatmentPlans?.on) {
      navigate(`/${slug}/treatment-plans`);
    } else if (perms.employees?.on) {
      navigate(`/${slug}/employees`);
    } else if (perms.services?.on) {
      navigate(`/${slug}/services`);
    } else if (perms.serviceGroups?.on) {
      navigate(`/${slug}/service-groups`);
    } else if (perms.serviceGroups?.on) {
      navigate(`/${slug}/service-groups`);
    } else if (perms.settings?.on) {
      navigate(`/${slug}/settings`);
    } else {
      setNoAccessAll(true);
      // Default fallback: no access at all → logout or 403
      // navigate(`/${slug}/no-access`);
    }
  }, [currentTenantAccess, slug, navigate]);

  useEffect(() => {
    if (DASHBOARD_TOUR_ENABLED && shouldRun) {
      setShowTourStart(true);
    }
  }, [shouldRun]);

  if (noAcesssAll)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.noAccess")} />
      </>
    );

  // console.log("Slug:", slug);
  // console.log("tenant in Dashboard", tenant);
  // console.log("userInfo in Dashboard", userInfo);
  // console.log("currentTenantAccess", currentTenantAccess);

  // console.log("hasDashboardAccess", hasDashboardAccess);

  if (hasDashboardAccess === false) return null;

  // LOGS

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.dashboard")}</title>
        <meta name="description" content={t("pageDescription.dashboard")} />
      </Helmet>

      {/* TOUR  */}
      {DASHBOARD_TOUR_ENABLED && (
        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous
          showSkipButton
          showProgress
          scrollToFirstStep
          disableScrolling
          styles={{
            options: {
              zIndex: 10000,
            },
          }}
          callback={(data) => {
            if (data.status === "finished" || data.status === "skipped") {
              markAsSeen();
              setRunTour(false);
            }
          }}
        />
      )}

      {DASHBOARD_TOUR_ENABLED && showTourStart && (
        <Flex
          position="fixed"
          bottom="2rem"
          right="2rem"
          bg="white"
          border="1px solid #E2E8F0"
          boxShadow="lg"
          borderRadius="md"
          p="1rem"
          zIndex={10001}
          maxW="320px"
          direction="column"
        >
          <Heading fontSize="md" mb="0.5rem">
            Welcome!
          </Heading>
          <div style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>
            Would you like a quick tour of the dashboard features?
          </div>
          <Flex justify="flex-end" gap="0.5rem">
            <button
              onClick={() => {
                markAsSeen();
                setShowTourStart(false);
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "#718096",
                cursor: "pointer",
              }}
            >
              Dismiss
            </button>
            <button
              onClick={() => {
                setShowTourStart(false);
                setRunTour(true); // ← START TOUR
              }}
              style={{
                background: "#3182ce",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Start Tour
            </button>
          </Flex>
        </Flex>
      )}

      {/* MAIN  */}

      <Flex
        w="100%"
        direction="column"
        p={["0em 0rem", "0rem"]}
        bg={[backgroundLightMode, "transparent"]}
      >
        {/* <Heading
              fontSize={["1.125rem", "1.25rem"]}
              fontWeight="600"
              m="0 0 1rem"
            >
              {t("dashboard.title")}
            </Heading> */}

        <>
          <Flex
            w="100%"
            p="0 0rem 1rem"
            direction="column"
            // bg={backgroundLightMode}
            gap="1rem"
          >
            <Flex
              w="100%"
              align="center"
              justify="space-between"
              gap="1rem"
              direction={["column", "row"]}
            >
              <Flex
                align="center"
                w="100%"
                gap="1rem"
                direction={["column", "row"]}
              >
                <Flex
                  w={["100%", "300px"]}
                  minW={["100%", "300px"]}
                  className="dashboard__case-acceptance"
                >
                  <DashboardCaseAcceptance />
                </Flex>
                {!isMobile && (
                  <Flex w="100%" direction="column">
                    <DashboardMoreStats currency={tenant && tenant.currency} />
                  </Flex>
                )}
              </Flex>

              {!isMobile && (
                <Flex
                  w={["100%", "369px"]}
                  minW={["100%", "369px"]}
                  maxW={["100%", "369px"]}
                  className="dashboard__limits"
                >
                  <DashboardLimits
                    hasSubscriptionAccess={hasSubscriptionAccess}
                  />
                </Flex>
              )}
            </Flex>
            <Flex
              w="100%"
              align="flex-start"
              justify="space-between"
              gap="1rem"
              direction={["column", "row"]}
            >
              <Flex w="100%" align="flex-start" justify="space-between">
                <Flex
                  align="flex-start"
                  justify="space-between"
                  gap="1rem"
                  w="100%"
                  direction={["column", "row"]}
                >
                  <Flex
                    w={["100%", "300px"]}
                    minW={["100%", "300px"]}
                    maxW={["100%", "300px"]}
                    className="dashboard__plans-pie"
                  >
                    <DashboardTreatmentPlansNew
                      currency={tenant && tenant.currency}
                    />
                  </Flex>
                  <Flex w="100%" direction="column">
                    <DashboardMonthlyStacked
                      currency={tenant && tenant.currency}
                    />
                  </Flex>

                  <Flex
                    w={["100%", "369px"]}
                    minW={["100%", "369px"]}
                    maxW={["100%", "369px"]}
                    borderRadius="1rem"
                    bg={whiteMode}
                  >
                    <DashboardServices currency={tenant && tenant.currency} />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </>
      </Flex>
    </>
  );
};

export default DashboardNew;
